import { useEffect, useState, useRef, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, FileText, Video, Music, Eye, Clock, Users,
  Heart, ThumbsUp, Share2, Star, Megaphone, ChevronRight, ExternalLink,
} from 'lucide-react'
import { contentApi, type ContentDetail, type ContentListItem } from '../../lib/api'
import { sanitizeHtml } from '../../lib/sanitize'
import { useExternalArticle } from '../../hooks/useExternalArticle'
import EmptyState from '../../components/EmptyState'
import CommentSection from '../../components/CommentSection'
import { usePageTitle } from '../../hooks/usePageTitle'

const FONT_KEY = 'knowledgeFontSize'
const FONT_STEPS = [13, 15, 17, 19] // 小 / 标准 / 大 / 特大
const FONT_LABELS = ['小', '标准', '大', '特大']

// 每日阅读奖励圆环动效：入场弹跳 / 可领取呼吸光环 / 蓄力中轻微呼吸
const RING_ANIMATION_CSS = `
@keyframes rr-pop-in {
  0% { transform: scale(0.3); opacity: 0; }
  60% { transform: scale(1.12); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes rr-pulse-ring {
  0% { transform: scale(1); opacity: 0.55; }
  70% { transform: scale(1.45); opacity: 0; }
  100% { transform: scale(1.45); opacity: 0; }
}
@keyframes rr-breath {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.06); }
}
@keyframes rr-charging {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.04); }
}
@keyframes rr-shine {
  0% { stroke-dashoffset: 190; opacity: 0; }
  15% { opacity: 0.9; }
  85% { opacity: 0.9; }
  100% { stroke-dashoffset: -190; opacity: 0; }
}
.rr-float-ring { animation: rr-pop-in 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
.rr-pulse-ring { background: radial-gradient(circle, rgba(245,158,11,0.45) 0%, rgba(245,158,11,0) 70%); animation: rr-pulse-ring 1.6s ease-out infinite; }
.rr-glow { animation: rr-pop-in 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both, rr-breath 1.8s ease-in-out 0.45s infinite; box-shadow: 0 0 12px rgba(245,158,11,0.55); }
.rr-charging { animation: rr-pop-in 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both, rr-charging 2.4s ease-in-out 0.45s infinite; }
.rr-shine-arc { animation: rr-shine 2.8s linear 0.45s infinite; }
`

const KnowledgeDetail = () => {
  usePageTitle()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // 外链文章：通过分享链接/收藏直接打开详情 URL 时，也要遵守同一套跳转规则
  // （公众号直跳 / 微信内非公众号链接引导复制），见 lib/externalLink.ts
  const { openLink, dialog: externalDialog } = useExternalArticle()
  const autoLinkRef = useRef<string | null>(null)
  const [detail, setDetail] = useState<ContentDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [readPercent, setReadPercent] = useState(0)
  const [counts, setCounts] = useState({ like: 0, favorite: 0, helpful: 0, share: 0 })
  const [activeFlags, setActiveFlags] = useState({ like: false, favorite: false, helpful: false, share: false })
  const [rewardNotice, setRewardNotice] = useState('')
  // 每日阅读奖励：圆环蓄力 → 手动领取（claimed 后当日不再展示圆环）
  const [reward, setReward] = useState<{
    status: 'disabled' | 'locked' | 'claimable' | 'claimed'
    daily_read_seconds: number
    required_seconds: number
    points: number
  } | null>(null)
  const [claiming, setClaiming] = useState(false)
  const startTimeRef = useRef<number>(Date.now())
  const readPercentRef = useRef(0)
  // 正文字号调节（持久化到 localStorage）
  const [fontStep, setFontStep] = useState<number>(() => {
    const saved = Number(localStorage.getItem(FONT_KEY))
    return FONT_STEPS.indexOf(saved) >= 0 ? FONT_STEPS.indexOf(saved) : 1
  })
  const fontSize = FONT_STEPS[fontStep]

  useEffect(() => {
    localStorage.setItem(FONT_KEY, String(fontSize))
  }, [fontSize])

  useEffect(() => {
    if (!id) return
    window.scrollTo(0, 0)
    setLoading(true)
    setError('')
    setReadPercent(0)
    startTimeRef.current = Date.now()
    contentApi
      .getDetail(id)
      .then((d) => {
        setDetail(d)
        contentApi.recordView(id).catch(() => {})
        // 获取当前用户互动状态
        contentApi.getInteractionStatus(id).then((s) => {
          setActiveFlags({ like: s.liked, favorite: s.favorited, helpful: s.helpful, share: s.shared })
          if (s.counts) setCounts(s.counts)
        }).catch(() => {})
      })
      .catch((e) => setError(e?.message || '加载失败'))
      .finally(() => setLoading(false))
  }, [id])

  /* 外链文章自动跳转：仅在拿到「带外链的内容」时触发一次。
     用 ref 记住已处理的内容 id —— 否则用户关掉复制弹窗后，
     任何一次 state 更新都会把它重新弹出来。 */
  useEffect(() => {
    const url = (detail?.link_url || '').trim()
    if (!detail || !url) return
    if (autoLinkRef.current === detail.id) return
    autoLinkRef.current = detail.id
    openLink(url)
  }, [detail?.id, detail?.link_url, openLink])

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement
      const max = doc.scrollHeight - doc.clientHeight
      if (max <= 0) {
        readPercentRef.current = 100
        setReadPercent(100)
        return
      }
      const cur = doc.scrollTop
      const pct = Math.min(100, Math.round((cur / max) * 100))
      readPercentRef.current = pct
      setReadPercent(pct)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [detail])

  // 上报阅读进度：每 15 秒心跳一次（同步当日阅读进度到圆环），离开页面时再补一次
  const flushReadProgress = (final: boolean) => {
    if (!id) return
    const seconds = Math.round((Date.now() - startTimeRef.current) / 1000)
    contentApi
      .updateReadProgress(id, {
        read_seconds: seconds,
        read_percent: readPercentRef.current,
        device_type: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'pc',
      })
      .then((r) => {
        if (r && r.status && r.status !== 'disabled') {
          setReward({
            status: r.status,
            daily_read_seconds: r.daily_read_seconds || 0,
            required_seconds: r.required_seconds || 0,
            points: r.points || 0,
          })
        }
      })
      .catch(() => {})
  }

  const showRewardToast = (text: string) => {
    setRewardNotice(text)
    window.setTimeout(() => setRewardNotice(''), 6000)
  }

  // 手动领取当日阅读奖励
  const handleClaimReward = async () => {
    if (claiming || !reward || reward.status !== 'claimable') return
    setClaiming(true)
    try {
      const r = await contentApi.claimReadReward()
      setReward(prev => prev ? { ...prev, status: 'claimed' } : prev)
      showRewardToast(`领取成功，${r.points} 积分已到账`)
    } catch (e: any) {
      // 已领取 / 未达标等异常时静默回落，并刷新一次状态
      contentApi.getReadRewardStatus().then(s => {
        if (s && s.status) setReward({ status: s.status, daily_read_seconds: s.daily_read_seconds || 0, required_seconds: s.required_seconds || 0, points: s.points || 0 })
      }).catch(() => {})
    } finally {
      setClaiming(false)
    }
  }

  useEffect(() => {
    if (!id) return
    startTimeRef.current = Date.now()
    readPercentRef.current = 0
    // 进入页面先拉一次当日奖励状态（未登录/关闭时静默）
    contentApi.getReadRewardStatus().then(s => {
      if (s && s.status) {
        setReward({
          status: s.status,
          daily_read_seconds: s.daily_read_seconds || 0,
          required_seconds: s.required_seconds || 0,
          points: s.points || 0,
        })
      }
    }).catch(() => {})
    const timer = window.setInterval(() => flushReadProgress(false), 15000)
    return () => {
      window.clearInterval(timer)
      flushReadProgress(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])


  const handleInteract = async (action: 'like' | 'favorite' | 'helpful' | 'share') => {
    if (!detail) return
    try {
      const r = await contentApi.interact(detail.id, { action })
      setActiveFlags(prev => ({ ...prev, [action]: r.active }))
      setCounts(prev => ({ ...prev, [action]: r.count }))
    } catch (e: any) {
      console.error('Interact failed', e)
    }
  }

  const handleShare = async () => {
    if (!detail) return
    try {
      // 复制链接到剪贴板
      const url = window.location.href
      await navigator.clipboard.writeText(url).catch(() => {})
      const r = await contentApi.shareCallback(detail.id)
      setCounts(prev => ({ ...prev, share: r.count }))
    } catch (e) {
      console.error('Share failed', e)
    }
  }

  if (loading) return <div className="p-5"><EmptyState variant="loading" /></div>

  if (error) {
    return (
      <div className="p-5 space-y-3">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" />返回列表
        </button>
        <EmptyState variant="error" description={error} />
      </div>
    )
  }

  if (!detail) return null

  const primaryMedia = (detail.attachments || []).find(a => a.is_primary)
  const isVideo = detail.media_type === 'video' && primaryMedia
  const isAudio = detail.media_type === 'audio' && primaryMedia
  const isNotice = detail.parent_category_name === '区县公告' || detail.category_name === '区县公告'
  const dateStr = (detail.published_at || '').slice(0, 10)

  const actions: Array<{ key: 'like' | 'helpful' | 'favorite' | 'share'; icon: ReactNode; label: string; count: number; active: string; idle: string; onClick: () => void }> = [
    { key: 'like', icon: <ThumbsUp className="w-5 h-5" />, label: '点赞', count: counts.like || 0, active: 'text-blue-500', idle: 'text-gray-500', onClick: () => handleInteract('like') },
    { key: 'helpful', icon: <Star className="w-5 h-5" />, label: '有用', count: counts.helpful || 0, active: 'text-green-600', idle: 'text-gray-500', onClick: () => handleInteract('helpful') },
    { key: 'favorite', icon: <Heart className="w-5 h-5" />, label: '收藏', count: counts.favorite || 0, active: 'text-red-500', idle: 'text-gray-500', onClick: () => handleInteract('favorite') },
    { key: 'share', icon: <Share2 className="w-5 h-5" />, label: '分享', count: counts.share || 0, active: 'text-purple-500', idle: 'text-gray-500', onClick: handleShare },
  ]

  // 每日阅读奖励圆环（claimed 后当日不再展示）
  const showRewardRing = !!reward && reward.status !== 'disabled' && reward.status !== 'claimed'
  const ringProgress = reward && reward.required_seconds > 0
    ? Math.min(1, reward.daily_read_seconds / reward.required_seconds)
    : 0
  const ringMinutes = Math.floor((reward?.daily_read_seconds || 0) / 60)
  const needMinutes = Math.ceil((reward?.required_seconds || 0) / 60)
  const isClaimable = reward?.status === 'claimable'
  const RING_R = 26
  const RING_C = 2 * Math.PI * RING_R

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      {/* 外链跳转引导弹窗（仅「微信内 + 非公众号链接」时出现） */}
      {externalDialog}
      {/* 圆环动效样式 */}
      <style>{RING_ANIMATION_CSS}</style>
      {/* 顶部阅读进度条（固定） */}
      <div className="fixed top-0 left-0 right-0 h-0.5 z-50">
        <div
          className="h-full bg-green-500 transition-all duration-300"
          style={{ width: `${readPercent}%` }}
        />
      </div>

      {/* 阅读达标奖励提示（固定） */}
      {rewardNotice && (
        <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 bg-green-600 text-white text-[13px] font-medium pl-3 pr-4 py-2.5 rounded-full shadow-lg animate-[fadeIn_0.3s_ease]">
          <Star className="w-4 h-4 fill-current" />
          {rewardNotice}
        </div>
      )}

      {/* 每日阅读奖励计时圆环（固定左下角；领取后当日隐藏） */}
      {showRewardRing && (
        <button
          onClick={handleClaimReward}
          disabled={!isClaimable || claiming}
          className={`rr-float-ring fixed left-2.5 bottom-24 z-[60] w-[44px] h-[44px] flex flex-col items-center justify-center rounded-full transition-transform ${
            isClaimable
              ? 'cursor-pointer hover:scale-110 active:scale-95 rr-glow'
              : 'cursor-default rr-charging'
          }`}
          aria-label={isClaimable ? `领取 ${reward?.points} 积分` : '今日阅读进度'}
        >
          {/* 可领取时的呼吸光环 */}
          {isClaimable && <span className="rr-pulse-ring absolute inset-0 rounded-full" />}
          <svg viewBox="0 0 64 64" className="w-[44px] h-[44px] absolute inset-0">
            <circle cx="32" cy="32" r={RING_R} fill="none" stroke="#f1f5f9" strokeWidth="6" />
            <circle
              cx="32" cy="32" r={RING_R} fill="none"
              stroke={isClaimable ? '#f59e0b' : '#22c55e'}
              strokeWidth="6" strokeLinecap="round"
              strokeDasharray={RING_C}
              strokeDashoffset={RING_C * (1 - ringProgress)}
              transform="rotate(-90 32 32)"
              style={{ transition: 'stroke-dashoffset 0.6s ease' }}
            />
            {/* 蓄力中：沿圆环流动的高光弧 */}
            {!isClaimable && (
              <circle
                className="rr-shine-arc"
                cx="32" cy="32" r={RING_R} fill="none"
                stroke="rgba(34,197,94,0.65)"
                strokeWidth="6" strokeLinecap="round"
                strokeDasharray="14 150"
                transform="rotate(-90 32 32)"
              />
            )}
          </svg>
          <span className={`relative text-[10px] font-bold leading-none ${isClaimable ? 'text-amber-600' : 'text-green-600'}`}>
            {isClaimable ? `+${reward?.points}` : `${ringMinutes}′`}
          </span>
          <span className="relative text-[7px] text-gray-400 mt-0.5 leading-none">
            {isClaimable ? '领积分' : `/${needMinutes}分`}
          </span>
        </button>
      )}

      {/* 浏览器历史返回（返回列表可能丢失来源时兜底） */}
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-[13px] text-gray-500 hover:text-gray-800"
        >
          <ArrowLeft className="w-4 h-4" />返回列表
        </button>
      </div>

      {/* 正文容器：浅色背景、经典文章式 */}
      <div className="max-w-3xl mx-auto px-4 mt-3 space-y-3">
        {/* 标题 + 元信息 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-4 sm:px-6 sm:py-5 space-y-2.5">
          <div className="flex flex-wrap items-center gap-1.5">
            {detail.is_recommended === 1 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100">
                <Star className="w-2.5 h-2.5" />推荐
              </span>
            )}
            {isNotice && (
              <span className="inline-flex items-center gap-0.5 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100">
                <Megaphone className="w-2.5 h-2.5" />区县公告
              </span>
            )}
            {detail.parent_category_name && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{detail.parent_category_name}</span>
            )}
            {detail.category_name && detail.category_name !== detail.parent_category_name && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{detail.category_name}</span>
            )}
            {detail.audience_name && (
              <span className="inline-flex items-center gap-0.5 text-[10px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-600">
                <Users className="w-2.5 h-2.5" />{detail.audience_name}
              </span>
            )}
          </div>
          <h1 className="text-xl font-bold text-gray-900 leading-snug">{detail.title}</h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
            {detail.author && <span>作者：{detail.author}</span>}
            {detail.source && <span>来源：{detail.source}</span>}
            {dateStr && (
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{dateStr}</span>
            )}
            <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" />{detail.view_count || 0} 阅读</span>
          </div>
        </div>

        {/* 外链文章：手动入口。自动跳转被浏览器拦截、或用户关掉复制弹窗后，还能再点 */}
        {detail.link_url && (
          <button
            onClick={() => openLink(detail.link_url!)}
            className="w-full flex items-center justify-center gap-1.5 bg-white rounded-2xl shadow-sm border border-blue-100 text-blue-600 text-[13px] font-medium py-3 hover:bg-blue-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            阅读原文
          </button>
        )}

        {/* 音频播放器 */}
        {isAudio && (
          <div className="flex gap-3 items-center bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-white flex items-center justify-center flex-shrink-0">
              <Music className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{detail.title}</p>
              <audio src={primaryMedia.file_url} controls preload="metadata" className="w-full mt-1.5 h-8" />
            </div>
          </div>
        )}

        {/* 视频 */}
        {isVideo && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2.5">
            <video src={primaryMedia.file_url} controls preload="metadata" className="w-full rounded-xl max-h-[55vh] bg-black" />
          </div>
        )}

        {/* 正文卡片 + 顶部字号调节 */}
        {detail.body && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-4 sm:px-6 sm:py-5">
            {/* 字号调节工具栏 */}
            <div className="flex items-center justify-between pb-2.5 mb-3.5 border-b border-gray-100">
              <span className="text-[11px] text-gray-400">字体大小 · {FONT_LABELS[fontStep]}</span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setFontStep(s => Math.max(0, s - 1))}
                  disabled={fontStep === 0}
                  className="text-xs px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-700 disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:text-gray-600 transition-colors"
                >
                  A−
                </button>
                <button
                  onClick={() => setFontStep(s => Math.min(FONT_STEPS.length - 1, s + 1))}
                  disabled={fontStep === FONT_STEPS.length - 1}
                  className="text-xs px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-700 disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:text-gray-600 transition-colors"
                >
                  A+
                </button>
              </div>
            </div>

            {/* 摘要不再在详情页展示（2026-09-19 业务要求）：简介只用于列表卡片
                与分享场景，正文前的摘要块视觉上与公众号排版重复 */}
            <article
              className="content-body prose prose-sm max-w-none break-words text-gray-800 leading-[1.85]"
              style={{ fontSize: `${fontSize}px` }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(detail.body) }}
            />
          </div>
        )}

        <CommentSection contentId={detail.id} isLoggedIn={!!localStorage.getItem('token')} />

        {/* 相关阅读（单列紧凑列表，无封面图标兜底） */}
        {detail.related && detail.related.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-4 sm:px-6">
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-1">
              <ChevronRight className="w-4 h-4" />相关阅读
            </h3>
            <div className="space-y-3.5">
              {detail.related.slice(0, 4).map((r: ContentListItem) => {
                const RIcon = r.media_type === 'video' ? Video : r.media_type === 'audio' ? Music : FileText
                const rDate = (r.published_at || r.created_at || '').slice(0, 10)
                return (
                  <button
                    key={r.id}
                    onClick={() => navigate(`/user/knowledge/${r.id}`)}
                    className="w-full flex gap-3 items-center text-left group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-gray-800 leading-snug line-clamp-2 group-hover:text-green-700 transition-colors">{r.title}</p>
                      <p className="text-[11px] text-gray-400 mt-1">{r.view_count || 0} 阅读 · {rDate}</p>
                    </div>
                    {r.cover_url ? (
                      <img src={r.cover_url} alt={r.title} className="w-20 h-14 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                    ) : (
                      <div className={`w-20 h-14 rounded-lg flex-shrink-0 flex items-center justify-center ${r.media_type === 'video' ? 'bg-purple-50 text-purple-400' : r.media_type === 'audio' ? 'bg-orange-50 text-orange-400' : 'bg-blue-50 text-blue-400'}`}>
                        <RIcon className="w-5 h-5" />
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* 文末互动行 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-4 flex items-center justify-around">
          {actions.map(a => (
            <button
              key={a.key}
              onClick={a.onClick}
              className={`flex flex-col items-center gap-1 text-[11px] transition-colors ${activeFlags[a.key] ? a.active : a.idle} hover:opacity-80`}
            >
              {a.icon}
              <span>{a.count} {a.label}</span>
            </button>
          ))}
        </div>

        <div className="text-center text-xs text-gray-400 pt-3 pb-2">
          © {new Date().getFullYear()} {localStorage.getItem('systemName') || '眉山疾控'}
        </div>
      </div>
    </div>
  )
}

export default KnowledgeDetail
